import { supabase } from '@/lib/supabase';

export const NOMBRE_FARMACIA = 'AMZEDEL';
export const SISTEMA_NOMBRE = 'FarmaAdmin';
export const SISTEMA_DESCRIPCION = 'Sistema de Gestión Farmacéutica';

/**
 * URL pública del logo de AMZEDEL (Copa de Higía).
 * Se usa para mostrar el logo en la interfaz (etiquetas <img>).
 * Para los PDFs se obtiene vía la función segura `logo-amzedel` para
 * evitar bloqueos por CORS, con esta URL como respaldo.
 */
export const AMZEDEL_LOGO_URL =
  'https://static.readdy.ai/image/5e0dc67cda370742b3d5bcd513fed04e/a51614cef519a5312bc2799dce5227cb.png';

export interface LogoInfo {
  dataUrl: string;
  width: number;
  height: number;
  format: 'PNG' | 'JPEG';
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('No se pudo leer el logo'));
    reader.readAsDataURL(blob);
  });
}

function imagenDims(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || 1, height: img.naturalHeight || 1 });
    img.onerror = () => resolve({ width: 1, height: 1 });
    img.src = dataUrl;
  });
}

let logoCache: LogoInfo | null | undefined;

/**
 * Obtiene el logo de AMZEDEL como dataURL listo para incrustar en jsPDF.
 * 1) Intenta la función de Supabase (sin CORS).
 * 2) Si falla, intenta leer la URL pública directamente.
 * 3) Si todo falla, devuelve null (se usa el monograma de respaldo).
 */
export async function cargarLogoAmzedel(): Promise<LogoInfo | null> {
  if (logoCache !== undefined) return logoCache;

  try {
    const { data, error } = await supabase.functions.invoke('logo-amzedel');
    const payload = data as { dataUrl?: string; format?: string } | null;
    if (!error && payload?.dataUrl) {
      const dims = await imagenDims(payload.dataUrl);
      logoCache = {
        dataUrl: payload.dataUrl,
        width: dims.width,
        height: dims.height,
        format: payload.format === 'JPEG' ? 'JPEG' : 'PNG',
      };
      return logoCache;
    }
  } catch {
    // seguimos con el respaldo
  }

  if (AMZEDEL_LOGO_URL) {
    try {
      const res = await fetch(AMZEDEL_LOGO_URL);
      if (res.ok) {
        const blob = await res.blob();
        const dataUrl = await blobToDataUrl(blob);
        const dims = await imagenDims(dataUrl);
        const format: 'PNG' | 'JPEG' =
          blob.type.includes('jpeg') || blob.type.includes('jpg') ? 'JPEG' : 'PNG';
        logoCache = { dataUrl, width: dims.width, height: dims.height, format };
        return logoCache;
      }
    } catch {
      // sin conexión o CORS: caemos al monograma
    }
  }

  logoCache = null;
  return null;
}